/**
 * Coverage caveats: what the run is known not to have seen — unmet probes,
 * unfired listeners, absent decoys, date gates, sensitive reads followed by
 * network activity without a canary hit, limits hit (docs/event-model.md §5,
 * ADR-0014). Owned by Phase 4 lane C.
 *
 * Dynamic analysis can't prove the absence of behavior; these notes name
 * the branches we can tell weren't taken, and how to take them.
 */
import { DECOY_PRESETS, KNOWN_DECOYS } from "../environments/browser/profiles";
import type { AuditEvent } from "../events";
import { isoDay, listenedType } from "./rules/escalation";
import type { AnalysisInput, CoverageNote, Finding } from "./types";

const WINDOW_PREFIX = "window.";
const unique = <T>(items: Iterable<T>): T[] => [...new Set(items)];
const seqs = (events: readonly AuditEvent[]) => events.map((e) => e.seq);
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const list = (names: readonly string[], max = 6) =>
  names.slice(0, max).join(", ") + (names.length > max ? `, … (${names.length - max} more)` : "");

/** The top-level name of a decoy (`chrome.runtime` → `chrome`). */
const topName = (path: string) => path.split(".")[0] ?? path;

function limitNotes({ events, limitsHit }: AnalysisInput): CoverageNote[] {
  return limitsHit.map((limit) => ({
    id: "limit-hit",
    title: `The run hit its ${limit} limit`,
    detail:
      "Recording or execution stopped at the limit, so anything the script would have done afterwards is missing from this report.",
    evidence: seqs(
      events.filter((e) => e.category === "lifecycle" && e.op === "limit" && e.target === limit),
    ),
  }));
}

function dateGateDetail(date: string, now: number, end: number): string {
  const at = date === "" ? Number.NaN : Date.parse(date);
  const from = isoDay(now);
  if (Number.isNaN(at)) {
    return "The script compares the current date with a date Afterimage couldn't work out. Try runs with the context date set on either side of it.";
  }
  if (at > now && at <= end) {
    return `The virtual clock crossed ${date} during this run, so checks made before it saw one side and checks after it saw the other. Code that only checks once may still hide a branch.`;
  }
  if (at > end) {
    return `This run's clock never reached ${date} (it started on ${from}). Set the context date after ${date} to see the other branch.`;
  }
  return `This run started on ${from}, after ${date}. Set the context date before ${date} to see the other branch.`;
}

function dateGateNotes(
  { events, context }: AnalysisInput,
  findings: readonly Finding[],
): CoverageNote[] {
  const end = context.now + events.reduce((max, e) => Math.max(max, e.t), 0);
  const byDate = new Map<string, number[]>();
  for (const gate of findings) {
    if (gate.rule !== "escalation.date-gate") continue;
    const key = gate.target ?? "";
    byDate.set(key, unique([...(byDate.get(key) ?? []), ...gate.evidence]));
  }
  return [...byDate].map(([date, evidence]) => ({
    id: "date-gate",
    title: `Date gate: ${date === "" ? "an unknown date" : date}`,
    detail: dateGateDetail(date, context.now, end),
    evidence: [...evidence].sort((a, b) => a - b),
  }));
}

function probeNotes({ events, context }: AnalysisInput): CoverageNote[] {
  const unmet = events.filter(
    (e) =>
      e.category === "probe" &&
      (e.op === "get" || e.op === "has") &&
      e.target.startsWith(WINDOW_PREFIX) &&
      !e.tags.includes("decoy"),
  );
  const nameOf = (e: AuditEvent) => topName(e.target.slice(WINDOW_PREFIX.length));
  const knownDecoys = KNOWN_DECOYS.map(topName);
  const enabled = context.decoys.map(topName);
  const isAbsentDecoy = (e: AuditEvent) =>
    knownDecoys.includes(nameOf(e)) && !enabled.includes(nameOf(e));
  const notes: CoverageNote[] = [];
  const decoys = unmet.filter(isAbsentDecoy);
  if (decoys.length > 0) {
    const names = unique(decoys.map(nameOf));
    const presets = unique(
      Object.values(DECOY_PRESETS)
        .filter((p) => p.decoys.some((d) => names.includes(topName(d))))
        .map((p) => p.label),
    );
    notes.push({
      id: "absent-decoys",
      title: `Checked for ${plural(names.length, "decoy")} this run didn't provide: ${list(names)}`,
      detail: `Code that only runs when these exist (or that stays quiet when they do) wasn't observed. Turn on the matching decoys${presets.length > 0 ? ` (${presets.join(", ")})` : ""} in the Context drawer and run again.`,
      evidence: seqs(decoys),
    });
  }
  const others = unmet.filter((e) => !isAbsentDecoy(e));
  if (others.length > 0) {
    const names = unique(others.map(nameOf));
    notes.push({
      id: "unmet-probes",
      title: `Looked for ${plural(names.length, "global")} that ${names.length === 1 ? "wasn't" : "weren't"} there: ${list(names)}`,
      detail:
        "Whatever the script does when these globals exist wasn't observed. Afterimage can't fake globals it doesn't know about.",
      evidence: seqs(others),
    });
  }
  return notes;
}

/** Event types that only user activity (or page lifecycle the run doesn't simulate) fires. */
const USER_EVENTS = new Set([
  "click",
  "dblclick",
  "auxclick",
  "contextmenu",
  "mousedown",
  "mouseup",
  "mousemove",
  "mouseover",
  "mouseout",
  "mouseenter",
  "mouseleave",
  "pointerdown",
  "pointerup",
  "pointermove",
  "touchstart",
  "touchend",
  "touchmove",
  "keydown",
  "keyup",
  "keypress",
  "input",
  "beforeinput",
  "change",
  "submit",
  "formdata",
  "reset",
  "focus",
  "blur",
  "focusin",
  "focusout",
  "paste",
  "copy",
  "cut",
  "scroll",
  "wheel",
  "resize",
  "visibilitychange",
  "beforeunload",
  "unload",
  "pagehide",
  "hashchange",
  "popstate",
  "storage",
]);

function listenerNotes({ events }: AnalysisInput): CoverageNote[] {
  const listeners = events.filter((e) => {
    const type = listenedType(e);
    return type !== null && USER_EVENTS.has(type);
  });
  if (listeners.length === 0) return [];
  const described = unique(listeners.map((e) => `${listenedType(e)} on ${e.node ?? e.target}`));
  return [
    {
      id: "unfired-listeners",
      title: `${plural(listeners.length, "listener")} for user activity never fired: ${list(described, 4)}`,
      detail:
        "The run simulates no user activity (typing, clicks, submits, navigation), so what these handlers do wasn't observed. Skimmers typically act inside them.",
      evidence: seqs(listeners),
    },
  ];
}

const WEB_STORAGE_READ = /^(localStorage|sessionStorage)(\.(getItem|key))?$/;

/** Reads of data a canary stands in for: cookies, Web Storage, sensitive form fields. */
const isSensitiveRead = (e: AuditEvent) =>
  e.tags.includes("cookie-read") ||
  e.tags.includes("form-read") ||
  (e.category === "storage" && WEB_STORAGE_READ.test(e.target));

function flowNotes({ events, flows, registry }: AnalysisInput): CoverageNote[] {
  const read = events.find(isSensitiveRead);
  if (!read) return [];
  const later = events.filter((e) => e.seq > read.seq && e.network !== undefined);
  if (later.length === 0) return [];
  const hits = new Set(flows.map((f) => f.seq));
  if (later.some((e) => hits.has(e.seq) || (e.canaries?.length ?? 0) > 0)) return [];
  const planted = registry.canaries.length > 0;
  return [
    {
      id: "unscanned-flow",
      title: `Read sensitive data, then made ${plural(later.length, "request")} without a canary in ${later.length === 1 ? "it" : "them"}`,
      detail: planted
        ? "No planted canary showed up in a request, in any encoding Afterimage scans for. The data may not have been sent, or it was transformed beyond the known encodings (custom XOR, compression, encryption)."
        : "No canaries were planted in this run, so whether the data left in these requests (in any encoding) wasn't tracked.",
      evidence: [read.seq, ...seqs(later)],
    },
  ];
}

function codeNotes({ events }: AnalysisInput): CoverageNote[] {
  const notes: CoverageNote[] = [];
  const unparsed = events.filter((e) => e.codegen?.parseError !== undefined);
  if (unparsed.length > 0) {
    notes.push({
      id: "uninstrumented-code",
      title: `${plural(unparsed.length, "dynamic payload")} didn't parse and ran unobserved`,
      detail:
        "Afterimage couldn't instrument the payload, so it ran as is: what it did may be missing or attributed to its caller.",
      evidence: seqs(unparsed),
    });
  }
  const stubbed = events.filter((e) => e.codegen?.module?.stubbed);
  if (stubbed.length > 0) {
    const names = unique(stubbed.map((e) => e.codegen?.module?.specifier ?? e.target));
    notes.push({
      id: "stubbed-modules",
      title: `${plural(stubbed.length, "import")} ${stubbed.length === 1 ? "was" : "were"} served by a recording stub: ${list(names)}`,
      detail:
        "The real packages never ran: calls into them are recorded, but whatever they would do themselves isn't.",
      evidence: seqs(stubbed),
    });
  }
  return notes;
}

/** The run's coverage caveats, most consequential first. */
export function coverageNotes(input: AnalysisInput, findings: readonly Finding[]): CoverageNote[] {
  return [
    ...limitNotes(input),
    ...dateGateNotes(input, findings),
    ...probeNotes(input),
    ...listenerNotes(input),
    ...flowNotes(input),
    ...codeNotes(input),
  ];
}
